import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { DrawerFormLayout, DrawerFormRow } from '@/components/forms/drawer-form-layout'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { SearchInput } from '@/components/forms/search-input'
import { Select } from '@/components/forms/select'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Drawer } from '@/components/overlays/drawer'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { cn } from '@/lib/cn'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionDisableIconUrl from '@/assets/action-menu/disable.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import chevronExpandIconUrl from '@/assets/admin-organization/chevron-expand.svg'
import folderEmptyIconUrl from '@/assets/admin-organization/folder-empty.svg'
import folderOrganizationIconUrl from '@/assets/admin-organization/folder-organization.svg'

import {
  addMemberToDepartment,
  batchUpdateUserStatus,
  createOrganizationDepartment,
  deleteOrganizationDepartment,
  getOrganizationSettings,
  getOrganizationTree,
  listMemberAccounts,
  listOrganizationCompanies,
  removeMemberFromDepartment,
  renameOrganizationDepartment,
  updateOrganizationSettings,
  type AdminMemberAccount,
  type AdminOrganizationSettings,
} from './api'
import {
  findDepartment,
  flattenDepartments,
  normalizeDepartmentTree,
  type OrganizationDepartmentNode,
} from './model'
import { useOrganizationSyncStatus } from './sync-status'

type DepartmentDialogMode = 'create' | 'rename'

const pageSize = 20

interface OrganizationSettingsFormState {
  organizationName: string
}

const emptyOrganizationSettingsForm: OrganizationSettingsFormState = {
  organizationName: '',
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
    </svg>
  )
}

function SettingsGlyph() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M6.9 2.1h2.2l.35 1.55c.35.13.69.32 1 .57l1.5-.48 1.1 1.9-1.15 1.08c.03.2.05.41.05.63s-.02.43-.05.64l1.15 1.07-1.1 1.9-1.5-.48c-.31.25-.65.44-1 .57L9.1 13h-2.2l-.35-1.55a4.16 4.16 0 0 1-1-.57l-1.5.48-1.1-1.9 1.15-1.07a4.1 4.1 0 0 1 0-1.27L2.95 5.64l1.1-1.9 1.5.48c.31-.25.65-.44 1-.57L6.9 2.1Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.25"
      />
      <circle cx="8" cy="7.35" r="1.75" stroke="currentColor" strokeWidth="1.25" />
    </svg>
  )
}

function FolderIcon({ organization }: { organization: boolean }) {
  return (
    <img
      alt=""
      aria-hidden
      className="h-3 w-3.5"
      src={organization ? folderOrganizationIconUrl : folderEmptyIconUrl}
    />
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <img
      alt=""
      aria-hidden
      className={cn(
        'h-1.5 w-2.5 transition-transform duration-200',
        open ? 'rotate-180' : 'rotate-90',
      )}
      src={chevronExpandIconUrl}
    />
  )
}

function UsersIcon() {
  return (
    <svg aria-hidden className="size-10" fill="none" viewBox="0 0 40 40">
      <path
        d="M20 20.5a5.25 5.25 0 1 0 0-10.5 5.25 5.25 0 0 0 0 10.5ZM11 29.5c1.55-3.8 4.78-5.7 9-5.7s7.45 1.9 9 5.7M31.4 22.6a4.3 4.3 0 0 0 0-8.2M34.1 29.1a8.2 8.2 0 0 0-4.85-4.75M8.6 22.6a4.3 4.3 0 0 1 0-8.2M5.9 29.1a8.2 8.2 0 0 1 4.85-4.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

function DepartmentMembersEmptyState({
  canAddMembers,
  isSearchActive,
  onAddMember,
}: {
  canAddMembers: boolean
  isSearchActive: boolean
  onAddMember: () => void
}) {
  const { t } = useTranslation('adminOrganization')

  return (
    <div className="flex flex-col items-center text-center">
      <span className="mb-3 text-[rgba(26,28,25,0.18)]">
        <UsersIcon />
      </span>
      <p className="text-sm font-medium text-[var(--color-text-muted)]">
        {isSearchActive
          ? t('organization.emptyMemberSearchResults')
          : t('organization.emptyMembers')}
      </p>
      {!isSearchActive && canAddMembers ? (
        <button
          className="mt-2 cursor-pointer rounded-sm text-sm font-medium text-[var(--color-primary)] underline underline-offset-4 transition-colors hover:text-[var(--color-primary-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
          onClick={onAddMember}
          type="button"
        >
          {t('organization.emptyMemberAddAction')}
        </button>
      ) : null}
    </div>
  )
}

function DepartmentTreeRow({
  department,
  expandedIds,
  onAdd,
  onDelete,
  onRename,
  onSelect,
  onToggle,
  readOnly,
  selectedId,
}: {
  department: OrganizationDepartmentNode
  expandedIds: Set<string>
  onAdd: (department: OrganizationDepartmentNode) => void
  onDelete: (department: OrganizationDepartmentNode) => void
  onRename: (department: OrganizationDepartmentNode) => void
  onSelect: (department: OrganizationDepartmentNode) => void
  onToggle: (departmentId: string) => void
  readOnly: boolean
  selectedId?: string
}) {
  const { t } = useTranslation('adminOrganization')
  const isSelected = selectedId === department.id
  const isExpanded = expandedIds.has(department.id)
  const canManage = !readOnly && department.depth > 0 && !department.isDefaultDepartment
  const canAddChild = !readOnly && !department.isDefaultDepartment
  const isOrganization = department.depth === 0 || department.type === 'company'

  return (
    <li>
      <div
        className={cn(
          'group relative flex h-[38px] min-w-0 items-center rounded-lg pr-1 text-sm transition-colors',
          isSelected
            ? 'bg-[rgba(25,91,255,0.1)] text-[var(--color-text)]'
            : 'hover:bg-[rgba(26,28,25,0.04)]',
        )}
        style={{ paddingLeft: 10 + department.depth * 14 }}
      >
        <button
          aria-label={department.name}
          className={cn(
            'flex size-6 cursor-pointer items-center justify-center rounded text-[rgba(26,28,25,0.56)] hover:bg-[var(--color-hover-muted)]',
            department.children.length === 0 ? 'invisible' : null,
          )}
          onClick={() => onToggle(department.id)}
          type="button"
        >
          <ChevronIcon open={isExpanded} />
        </button>
        <button
          className={cn(
            'flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left transition-[padding] duration-150',
            canManage
              ? 'group-hover:pr-16 group-focus-within:pr-16'
              : canAddChild
                ? 'group-hover:pr-9 group-focus-within:pr-9'
                : null,
          )}
          onClick={() => onSelect(department)}
          title={`${department.name} (${String(department.memberCount)})`}
          type="button"
        >
          <span className="shrink-0 text-[rgba(26,28,25,0.68)]">
            <FolderIcon organization={isOrganization} />
          </span>
          <span className="min-w-0 truncate">
            {department.name} ({department.memberCount})
          </span>
        </button>
        {canAddChild || canManage ? (
          <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
            {canAddChild ? (
              <button
                aria-label={t('actions.createDepartment')}
                className="flex size-7 cursor-pointer items-center justify-center rounded hover:bg-[var(--color-hover-muted)]"
                onClick={() => onAdd(department)}
                type="button"
              >
                <PlusIcon />
              </button>
            ) : null}
            {canManage ? (
              <ActionMenu
                ariaLabel={department.name}
                items={[
                  {
                    iconSrc: actionEditIconUrl,
                    key: 'edit',
                    label: t('actions.edit'),
                    onSelect: () => onRename(department),
                  },
                  {
                    iconSrc: actionDeleteIconUrl,
                    key: 'delete',
                    label: t('actions.delete'),
                    onSelect: () => onDelete(department),
                    variant: 'danger',
                  },
                ]}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {isExpanded && department.children.length > 0 ? (
        <ul>
          {department.children.map((child) => (
            <DepartmentTreeRow
              department={child}
              expandedIds={expandedIds}
              key={child.id}
              onAdd={onAdd}
              onDelete={onDelete}
              onRename={onRename}
              onSelect={onSelect}
              onToggle={onToggle}
              readOnly={readOnly}
              selectedId={selectedId}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function roleLabel(role: string | undefined, t: (key: string) => string) {
  if (role === 'admin') {
    return t('memberAccounts.role.admin')
  }

  if (role === 'user') {
    return t('memberAccounts.role.user')
  }

  return role?.trim() ? role : t('memberAccounts.role.unknown')
}

function settingsFormFromData(settings: AdminOrganizationSettings): OrganizationSettingsFormState {
  return {
    organizationName: settings.organization_name,
  }
}

export function AdminOrganizationDepartmentsPage() {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string> | null>(null)
  const [orgSearch, setOrgSearch] = useState('')
  const [memberSearch, setMemberSearch] = useState('')
  const [memberPage, setMemberPage] = useState(1)
  const [memberPageSize, setMemberPageSize] = useState(pageSize)
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set())
  const [departmentDialog, setDepartmentDialog] = useState<{
    department: OrganizationDepartmentNode
    mode: DepartmentDialogMode
    open: boolean
  } | null>(null)
  const [departmentName, setDepartmentName] = useState('')
  const [departmentToDelete, setDepartmentToDelete] = useState<OrganizationDepartmentNode | null>(
    null,
  )
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [memberToAdd, setMemberToAdd] = useState('')
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false)
  const [settingsFormDraft, setSettingsFormDraft] = useState<OrganizationSettingsFormState | null>(
    null,
  )
  const resetCompanyView = useCallback(() => {
    setSelectedDepartmentId(null)
    setExpandedIds(null)
    setMemberPage(1)
    setSelectedMembers(new Set())
    setDepartmentDialog(null)
    setDepartmentToDelete(null)
    setAddMemberOpen(false)
  }, [])

  const companiesQuery = useQuery({
    queryFn: listOrganizationCompanies,
    queryKey: ['admin-organization-companies'],
  })
  const settingsQuery = useQuery({
    enabled: settingsDrawerOpen,
    queryFn: getOrganizationSettings,
    queryKey: ['admin-organization-settings'],
  })
  const syncStatusQuery = useOrganizationSyncStatus()
  const isSyncing = syncStatusQuery.data?.is_syncing ?? false
  const companyId =
    companiesQuery.data?.find((company) => company.id === selectedCompanyId)?.id ??
    companiesQuery.data?.[0]?.id ??
    null
  const previousCompanyIdRef = useRef<string | null>(companyId)

  useEffect(() => {
    if (previousCompanyIdRef.current !== null && previousCompanyIdRef.current !== companyId) {
      setSelectedCompanyId(companyId)
      resetCompanyView()
    }
    previousCompanyIdRef.current = companyId
  }, [companyId, resetCompanyView])

  const treeQuery = useQuery({
    enabled: Boolean(companyId),
    queryFn: () => getOrganizationTree(companyId ?? ''),
    queryKey: ['admin-organization-tree', companyId],
  })
  const rootDepartment = useMemo(
    () => (treeQuery.data ? normalizeDepartmentTree(treeQuery.data) : null),
    [treeQuery.data],
  )
  const allDepartments = useMemo(() => flattenDepartments(rootDepartment), [rootDepartment])
  const defaultExpandedIds = useMemo(
    () => new Set(allDepartments.filter((item) => item.depth < 2).map((item) => item.id)),
    [allDepartments],
  )
  const visibleExpandedIds = expandedIds ?? defaultExpandedIds
  const defaultSelectedDepartmentId = rootDepartment?.id ?? null
  const selectedDepartment = useMemo(
    () =>
      selectedDepartmentId
        ? findDepartment(rootDepartment, selectedDepartmentId)
        : defaultSelectedDepartmentId
          ? findDepartment(rootDepartment, defaultSelectedDepartmentId)
          : rootDepartment,
    [defaultSelectedDepartmentId, rootDepartment, selectedDepartmentId],
  )
  const [previousTreeData, setPreviousTreeData] = useState(treeQuery.data)
  if (treeQuery.data !== previousTreeData) {
    setPreviousTreeData(treeQuery.data)
    if (
      treeQuery.isSuccess &&
      selectedDepartmentId !== null &&
      findDepartment(rootDepartment, selectedDepartmentId) === null
    ) {
      resetCompanyView()
    }
  }

  const filteredTree = useMemo(() => {
    const keyword = orgSearch.trim().toLowerCase()

    if (!keyword || !rootDepartment) {
      return rootDepartment
    }

    const filterNode = (node: OrganizationDepartmentNode): OrganizationDepartmentNode | null => {
      const children = node.children.map(filterNode).filter(Boolean) as OrganizationDepartmentNode[]
      const matched = node.name.toLowerCase().includes(keyword)

      if (!matched && children.length === 0) {
        return null
      }

      return {
        ...node,
        children,
      }
    }

    return filterNode(rootDepartment)
  }, [orgSearch, rootDepartment])
  const departmentMembersQuery = useQuery({
    enabled: Boolean(selectedDepartment),
    queryFn: () =>
      listMemberAccounts({
        orgUnitId: selectedDepartment?.id,
        page: memberPage,
        pageSize: memberPageSize,
        search: memberSearch,
      }),
    queryKey: [
      'admin-organization-member-accounts',
      'departments-page',
      selectedDepartment?.id,
      memberPage,
      memberPageSize,
      memberSearch,
    ],
  })
  const displayedMembers = useMemo(
    () => departmentMembersQuery.data?.items ?? [],
    [departmentMembersQuery.data?.items],
  )
  const displayedMemberIds = useMemo(
    () => displayedMembers.map((member) => member.id),
    [displayedMembers],
  )
  const allDisplayedMembersSelected =
    displayedMemberIds.length > 0 && displayedMemberIds.every((id) => selectedMembers.has(id))
  const showMemberSearchEmptyState = memberSearch.trim().length > 0
  const canAddMembersToSelectedDepartment = Boolean(
    selectedDepartment && !selectedDepartment.isDefaultDepartment,
  )
  const canRemoveSelectedMembersFromDepartment =
    Boolean(selectedDepartment && !selectedDepartment.isDefaultDepartment) &&
    selectedMembers.size > 0 &&
    displayedMembers.filter((member) => selectedMembers.has(member.id)).length ===
      selectedMembers.size &&
    displayedMembers
      .filter((member) => selectedMembers.has(member.id))
      .every((member) =>
        member.departments.some((department) => department.id === selectedDepartment?.id),
      )
  const candidateMembersQuery = useQuery({
    enabled: addMemberOpen,
    queryFn: () =>
      listMemberAccounts({
        page: 1,
        pageSize,
        search: candidateSearch,
        status: 'active',
      }),
    queryKey: ['admin-organization-member-candidates', candidateSearch, addMemberOpen],
  })
  const candidateMembers = useMemo(() => {
    const directMemberIds = new Set((selectedDepartment?.members ?? []).map((member) => member.id))

    return (candidateMembersQuery.data?.items ?? []).filter(
      (member) => !directMemberIds.has(member.id),
    )
  }, [candidateMembersQuery.data?.items, selectedDepartment?.members])

  const settingsDataForm = settingsQuery.data
    ? settingsFormFromData(settingsQuery.data)
    : emptyOrganizationSettingsForm
  const settingsForm = settingsFormDraft ?? settingsDataForm
  const updateSettingsForm = (
    updater: (current: OrganizationSettingsFormState) => OrganizationSettingsFormState,
  ) => {
    setSettingsFormDraft((current) => updater(current ?? settingsDataForm))
  }

  const invalidateOrganization = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-organization-tree'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-organization-member-accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-organization-member-candidates'] }),
    ])
  const showOperationFeedback = (tone: 'error' | 'success' | 'warning', message: string) => {
    showFeedback({
      message,
      tone,
    })
  }
  const openAddMemberDialog = () => {
    setCandidateSearch('')
    setMemberToAdd('')
    setAddMemberOpen(true)
  }

  const updateSettingsMutation = useMutation({
    mutationFn: () =>
      updateOrganizationSettings({
        organization_name: settingsForm.organizationName.trim(),
      }),
    onSuccess: async () => {
      setSettingsFormDraft(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-organization-settings'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-organization-companies'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-organization-tree'] }),
      ])
      showOperationFeedback('success', t('success.updateSettings'))
      setSettingsDrawerOpen(false)
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const createDepartmentMutation = useMutation({
    mutationFn: createOrganizationDepartment,
    onSuccess: async () => {
      await invalidateOrganization()
      showOperationFeedback('success', t('success.createDepartment'))
      setDepartmentDialog(null)
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const renameDepartmentMutation = useMutation({
    mutationFn: renameOrganizationDepartment,
    onSuccess: async () => {
      await invalidateOrganization()
      showOperationFeedback('success', t('success.renameDepartment'))
      setDepartmentDialog(null)
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const deleteDepartmentMutation = useMutation({
    mutationFn: deleteOrganizationDepartment,
    onSuccess: async () => {
      await invalidateOrganization()
      showOperationFeedback('success', t('success.deleteDepartment'))
      setDepartmentToDelete(null)
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const addMemberMutation = useMutation({
    mutationFn: addMemberToDepartment,
    onSuccess: async () => {
      await invalidateOrganization()
      showOperationFeedback('success', t('success.addMember'))
      setAddMemberOpen(false)
      setMemberToAdd('')
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const removeMembersMutation = useMutation({
    mutationFn: async ({ orgUnitId, userIds }: { orgUnitId: string; userIds: string[] }) => {
      const results = await Promise.allSettled(
        userIds.map((userId) =>
          removeMemberFromDepartment({
            orgUnitId,
            userId,
          }),
        ),
      )
      const succeededCount = results.filter((result) => result.status === 'fulfilled').length
      const failedCount = results.length - succeededCount

      if (succeededCount === 0) {
        throw new Error('all remove member requests failed')
      }

      return {
        failedCount,
        succeededCount,
      }
    },
    onSuccess: async (result) => {
      await invalidateOrganization()
      showOperationFeedback(
        result.failedCount > 0 ? 'warning' : 'success',
        result.failedCount > 0 ? t('errors.removeMemberPartial') : t('success.removeMember'),
      )
      setSelectedMembers(new Set())
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const batchDisableMutation = useMutation({
    mutationFn: (userIds: string[]) => batchUpdateUserStatus(userIds, 'banned'),
    onSuccess: async (result) => {
      await invalidateOrganization()
      if (result.code === 'partial_success') {
        showOperationFeedback('warning', t('errors.batchStatusPartial'))
      } else {
        showOperationFeedback('success', t('success.batchDisable'))
      }
      setSelectedMembers(new Set())
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const departmentToDeleteCanonical = useMemo(
    () =>
      departmentToDelete
        ? (findDepartment(rootDepartment, departmentToDelete.id) ?? departmentToDelete)
        : null,
    [departmentToDelete, rootDepartment],
  )
  const departmentDeleteBlockReason =
    departmentToDeleteCanonical?.depth === 0
      ? t('errors.deleteRootDepartment')
      : departmentToDeleteCanonical?.isDefaultDepartment
        ? t('errors.deleteDefaultDepartment')
        : departmentToDeleteCanonical && departmentToDeleteCanonical.children.length > 0
          ? t('errors.deleteDepartmentWithChildren')
          : departmentToDeleteCanonical && departmentToDeleteCanonical.members.length > 0
            ? t('errors.deleteDepartmentWithMembers')
            : null
  const memberColumns = useMemo<DataTableColumn<AdminMemberAccount>[]>(
    () => [
      {
        align: 'center',
        header: (
          <input
            aria-label={t('common.selectedCount', { count: selectedMembers.size })}
            checked={allDisplayedMembersSelected}
            className="size-3.5 accent-[var(--color-primary)]"
            disabled={isSyncing}
            onChange={(event) => {
              setSelectedMembers(event.target.checked ? new Set(displayedMemberIds) : new Set())
            }}
            type="checkbox"
          />
        ),
        id: 'selection',
        render: (member) => (
          <input
            aria-label={member.name}
            checked={selectedMembers.has(member.id)}
            className="size-3.5 accent-[var(--color-primary)]"
            disabled={isSyncing}
            onChange={(event) => {
              setSelectedMembers((current) => {
                const next = new Set(current)
                if (event.target.checked) {
                  next.add(member.id)
                } else {
                  next.delete(member.id)
                }
                return next
              })
            }}
            type="checkbox"
          />
        ),
        width: 58,
      },
      {
        header: t('organization.table.name'),
        id: 'name',
        render: (member) => member.name,
        width: 190,
      },
      {
        header: t('organization.table.email'),
        id: 'email',
        render: (member) => member.email || '-',
        width: 260,
      },
      {
        header: t('organization.table.role'),
        id: 'role',
        render: (member) => roleLabel(member.role, t),
        width: 150,
      },
      {
        align: 'center',
        header: t('organization.table.actions'),
        id: 'actions',
        pin: 'right',
        render: (member) =>
          isSyncing ||
          !selectedDepartment ||
          selectedDepartment.isDefaultDepartment ||
          !member.departments.some((department) => department.id === selectedDepartment.id) ? (
            <span className="text-[var(--color-text-muted)]">-</span>
          ) : (
            <ActionMenu
              ariaLabel={member.name}
              items={[
                {
                  iconSrc: actionDeleteIconUrl,
                  key: 'remove',
                  label: t('actions.deleteMember'),
                  onSelect: () => {
                    removeMembersMutation.mutate({
                      orgUnitId: selectedDepartment.id,
                      userIds: [member.id],
                    })
                  },
                  variant: 'danger',
                },
              ]}
            />
          ),
        width: 90,
      },
    ],
    [
      allDisplayedMembersSelected,
      displayedMemberIds,
      isSyncing,
      removeMembersMutation,
      selectedDepartment,
      selectedMembers,
      t,
    ],
  )

  const isLoading = companiesQuery.isLoading || treeQuery.isLoading
  const isError = companiesQuery.isError || treeQuery.isError

  return (
    <AdminShellPage activePath="/admin/organization/departments">
      <section
        aria-label={t('organization.workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-organization-workspace"
      >
        <PageHeader
          actions={
            <>
              {(companiesQuery.data?.length ?? 0) > 1 ? (
                <Select
                  aria-label={t('common.selectCompany')}
                  className="w-[220px]"
                  onValueChange={(value) => {
                    setSelectedCompanyId(value)
                    resetCompanyView()
                  }}
                  options={(companiesQuery.data ?? []).map((company) => ({
                    label: company.name,
                    value: company.id,
                  }))}
                  value={companyId ?? ''}
                />
              ) : null}
              <Button
                onClick={() => {
                  setSettingsFormDraft(null)
                  setSettingsDrawerOpen(true)
                }}
                size="sm"
                variant="secondary"
              >
                <SettingsGlyph />
                {t('actions.settings')}
              </Button>
            </>
          }
          description={t('organization.description')}
          title={t('organization.title')}
        />
        {isSyncing ? (
          <div className="mx-5 mb-3 rounded-lg bg-[#fff7e6] px-4 py-3 text-sm text-[#8a5100] lg:mx-[30px]">
            {t('errors.syncReadOnly')}
          </div>
        ) : null}
        <div className="grid min-h-0 flex-1 gap-3 px-5 pb-6 lg:grid-cols-[310px_minmax(0,1fr)] lg:px-[30px]">
          {isError ? (
            <div className="lg:col-span-2">
              <ErrorState
                action={
                  <Button
                    className="mt-3"
                    onClick={() => {
                      void companiesQuery.refetch()
                      void treeQuery.refetch()
                    }}
                    size="sm"
                    variant="secondary"
                  >
                    {t('actions.retry')}
                  </Button>
                }
                description={t('errors.loadDescription')}
                title={t('errors.loadTitle')}
              />
            </div>
          ) : (
            <>
              <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[var(--color-panel-border)] bg-white p-5 shadow-[var(--shadow-panel-subtle)]">
                <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
                  <h2 className="text-[15px] font-semibold tracking-[0.03em] text-[var(--color-text)]">
                    {t('organization.treeTitle')}
                  </h2>
                  {rootDepartment && !isSyncing ? (
                    <button
                      className="inline-flex cursor-pointer items-center gap-1 text-sm text-[var(--color-primary)]"
                      onClick={() => {
                        setDepartmentDialog({
                          department: rootDepartment,
                          mode: 'create',
                          open: true,
                        })
                        setDepartmentName('')
                      }}
                      type="button"
                    >
                      <PlusIcon />
                      {t('actions.createDepartment')}
                    </button>
                  ) : null}
                </div>
                <div className="shrink-0">
                  <SearchInput
                    aria-label={t('organization.orgSearchLabel')}
                    onChange={(event) => setOrgSearch(event.target.value)}
                    placeholder={t('organization.orgSearchPlaceholder')}
                    value={orgSearch}
                  />
                </div>
                <div className="organization-tree-scroll mt-4 min-h-0 flex-1 overflow-auto">
                  {isLoading ? (
                    <div className="grid gap-2">
                      {Array.from({ length: 8 }, (_, index) => (
                        <div
                          aria-hidden
                          className="h-[38px] animate-pulse rounded-lg bg-[var(--color-skeleton)]"
                          key={index}
                        />
                      ))}
                    </div>
                  ) : filteredTree ? (
                    <ul>
                      <DepartmentTreeRow
                        department={filteredTree}
                        expandedIds={visibleExpandedIds}
                        onAdd={(department) => {
                          setDepartmentDialog({ department, mode: 'create', open: true })
                          setDepartmentName('')
                        }}
                        onDelete={(department) =>
                          setDepartmentToDelete(
                            findDepartment(rootDepartment, department.id) ?? department,
                          )
                        }
                        onRename={(department) => {
                          setDepartmentDialog({ department, mode: 'rename', open: true })
                          setDepartmentName(department.name)
                        }}
                        onSelect={(department) => {
                          setSelectedDepartmentId(department.id)
                          setMemberPage(1)
                          setSelectedMembers(new Set())
                        }}
                        onToggle={(departmentId) => {
                          setExpandedIds((current) => {
                            const next = new Set(current ?? defaultExpandedIds)
                            if (next.has(departmentId)) {
                              next.delete(departmentId)
                            } else {
                              next.add(departmentId)
                            }
                            return next
                          })
                        }}
                        readOnly={isSyncing}
                        selectedId={selectedDepartment?.id}
                      />
                    </ul>
                  ) : (
                    <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
                      {t('errors.noCompany')}
                    </p>
                  )}
                </div>
              </aside>
              <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[var(--color-panel-border)] bg-white shadow-[var(--shadow-panel-subtle)]">
                <div className="flex shrink-0 flex-col gap-3 p-5 min-[1200px]:flex-row min-[1200px]:items-start min-[1200px]:justify-between">
                  <SearchInput
                    aria-label={t('organization.memberSearchLabel')}
                    className="w-full max-w-[360px] shrink-0 min-[1200px]:w-[220px] min-[1400px]:w-[270px]"
                    onChange={(event) => {
                      setMemberSearch(event.target.value)
                      setMemberPage(1)
                      setSelectedMembers(new Set())
                    }}
                    placeholder={t('organization.memberSearchPlaceholder')}
                    value={memberSearch}
                  />
                  <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
                    <Button
                      className="shrink-0 whitespace-nowrap"
                      disabled={
                        !canRemoveSelectedMembersFromDepartment || isSyncing || !selectedDepartment
                      }
                      onClick={() => {
                        if (!selectedDepartment) {
                          return
                        }
                        removeMembersMutation.mutate({
                          orgUnitId: selectedDepartment.id,
                          userIds: Array.from(selectedMembers),
                        })
                      }}
                      size="sm"
                      variant="secondary"
                    >
                      <img alt="" aria-hidden className="size-3.5" src={actionDeleteIconUrl} />
                      {t('actions.deleteMember')}
                    </Button>
                    <Button
                      className="shrink-0 whitespace-nowrap"
                      disabled={selectedMembers.size === 0 || isSyncing}
                      isLoading={batchDisableMutation.isPending}
                      onClick={() => batchDisableMutation.mutate(Array.from(selectedMembers))}
                      size="sm"
                      variant="danger"
                    >
                      <img
                        alt=""
                        aria-hidden
                        className="size-3.5 brightness-0 invert"
                        src={actionDisableIconUrl}
                      />
                      {t('actions.batchDisable')}
                    </Button>
                    <Button
                      className="shrink-0 whitespace-nowrap"
                      disabled={!canAddMembersToSelectedDepartment || isSyncing}
                      onClick={openAddMemberDialog}
                      size="sm"
                    >
                      <PlusIcon />
                      {t('actions.addMember')}
                    </Button>
                  </div>
                </div>
                <div className="flex min-h-0 flex-1 flex-col px-5 pb-5">
                  {departmentMembersQuery.isError ? (
                    <ErrorState
                      action={
                        <Button
                          className="mt-3"
                          onClick={() => {
                            void departmentMembersQuery.refetch()
                          }}
                          size="sm"
                          variant="secondary"
                        >
                          {t('actions.retry')}
                        </Button>
                      }
                      description={t('errors.loadDescription')}
                      title={t('errors.loadTitle')}
                    />
                  ) : (
                    <>
                      <div className="min-h-0 flex-1 overflow-auto">
                        <DataTable
                          columns={memberColumns}
                          empty={
                            <DepartmentMembersEmptyState
                              canAddMembers={canAddMembersToSelectedDepartment && !isSyncing}
                              isSearchActive={showMemberSearchEmptyState}
                              onAddMember={openAddMemberDialog}
                            />
                          }
                          getRowKey={(member) => member.id}
                          isLoading={isLoading || departmentMembersQuery.isLoading}
                          minWidth={918}
                          rows={displayedMembers}
                        />
                      </div>
                      <Pagination
                        disabled={departmentMembersQuery.isFetching}
                        labels={{
                          next: t('pagination.next'),
                          pageSize: (size) => t('pagination.pageSize', { count: size }),
                          previous: t('pagination.previous'),
                        }}
                        onPageChange={(nextPage) => {
                          setMemberPage(nextPage)
                          setSelectedMembers(new Set())
                        }}
                        onPageSizeChange={(nextPageSize) => {
                          setMemberPageSize(nextPageSize)
                          setMemberPage(1)
                          setSelectedMembers(new Set())
                        }}
                        page={memberPage}
                        pageSize={memberPageSize}
                        total={departmentMembersQuery.data?.total ?? 0}
                      />
                    </>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
        <Drawer
          footer={
            <>
              <Button
                disabled={updateSettingsMutation.isPending}
                onClick={() => {
                  setSettingsFormDraft(null)
                  setSettingsDrawerOpen(false)
                }}
                size="sm"
                variant="secondary"
              >
                {t('actions.cancel')}
              </Button>
              <Button
                disabled={settingsForm.organizationName.trim().length === 0}
                isLoading={updateSettingsMutation.isPending}
                onClick={() => updateSettingsMutation.mutate()}
                size="sm"
              >
                {t('actions.save')}
              </Button>
            </>
          }
          onOpenChange={(open) => {
            if (!open) {
              setSettingsFormDraft(null)
            }
            setSettingsDrawerOpen(open)
          }}
          open={settingsDrawerOpen}
          size="md"
          title={t('organization.settingsDrawer.title')}
        >
          {settingsQuery.isLoading ? (
            <div className="grid gap-5">
              {Array.from({ length: 7 }, (_, index) => (
                <div
                  aria-hidden
                  className="h-[38px] animate-pulse rounded-lg bg-[var(--color-skeleton)]"
                  key={index}
                />
              ))}
            </div>
          ) : settingsQuery.isError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => {
                    void settingsQuery.refetch()
                  }}
                  size="sm"
                  variant="secondary"
                >
                  {t('actions.retry')}
                </Button>
              }
              description={t('errors.loadDescription')}
              title={t('errors.loadTitle')}
            />
          ) : (
            <DrawerFormLayout className="mt-0">
              <DrawerFormRow columns={1}>
                <FormField
                  error={
                    settingsForm.organizationName.trim().length === 0
                      ? t('organization.settingsDrawer.validation.organizationNameRequired')
                      : undefined
                  }
                  htmlFor="admin-organization-settings-name"
                  label={t('organization.settingsDrawer.organizationName')}
                  labelExtra={
                    <HelpTooltip
                      label={t('organization.settingsDrawer.organizationNameTooltip')}
                      widthClassName="w-[280px]"
                    >
                      {t('organization.settingsDrawer.organizationNameTooltip')}
                    </HelpTooltip>
                  }
                  required
                >
                  <Input
                    aria-label={t('organization.settingsDrawer.organizationName')}
                    id="admin-organization-settings-name"
                    invalid={settingsForm.organizationName.trim().length === 0}
                    onChange={(event) =>
                      updateSettingsForm((current) => ({
                        ...current,
                        organizationName: event.target.value,
                      }))
                    }
                    value={settingsForm.organizationName}
                  />
                </FormField>
              </DrawerFormRow>
            </DrawerFormLayout>
          )}
        </Drawer>
        <Modal
          closeLabel={t('actions.cancel')}
          footer={
            <>
              <Button
                disabled={createDepartmentMutation.isPending || renameDepartmentMutation.isPending}
                onClick={() => setDepartmentDialog(null)}
                size="sm"
                variant="secondary"
              >
                {t('actions.cancel')}
              </Button>
              <Button
                disabled={departmentName.trim().length === 0 || isSyncing}
                isLoading={createDepartmentMutation.isPending || renameDepartmentMutation.isPending}
                onClick={() => {
                  if (!departmentDialog) {
                    return
                  }
                  if (departmentDialog.mode === 'create') {
                    createDepartmentMutation.mutate({
                      name: departmentName,
                      parentId: departmentDialog.department.id,
                    })
                  } else {
                    renameDepartmentMutation.mutate({
                      name: departmentName,
                      orgUnitId: departmentDialog.department.id,
                    })
                  }
                }}
                size="sm"
              >
                {t('actions.confirm')}
              </Button>
            </>
          }
          onClose={() => setDepartmentDialog(null)}
          open={Boolean(departmentDialog?.open)}
          subtitle={
            departmentDialog?.mode === 'create'
              ? t('organization.createDepartmentDialog.subtitle', {
                  name: departmentDialog.department.name,
                })
              : undefined
          }
          title={
            departmentDialog?.mode === 'create'
              ? t('organization.createDepartmentDialog.title')
              : t('organization.renameDepartmentDialog.title')
          }
        >
          <Input
            autoFocus
            aria-label={t('organization.createDepartmentDialog.nameLabel')}
            onChange={(event) => setDepartmentName(event.target.value)}
            placeholder={t('organization.createDepartmentDialog.namePlaceholder')}
            value={departmentName}
          />
        </Modal>
        <Modal
          closeLabel={t('actions.cancel')}
          footer={
            <>
              <Button
                disabled={deleteDepartmentMutation.isPending}
                onClick={() => setDepartmentToDelete(null)}
                size="sm"
                variant="secondary"
              >
                {t('actions.cancel')}
              </Button>
              <Button
                disabled={Boolean(departmentDeleteBlockReason) || isSyncing}
                isLoading={deleteDepartmentMutation.isPending}
                onClick={() => {
                  if (!departmentToDeleteCanonical) {
                    return
                  }
                  deleteDepartmentMutation.mutate(departmentToDeleteCanonical.id)
                }}
                size="sm"
                variant="danger"
              >
                {t('actions.delete')}
              </Button>
            </>
          }
          onClose={() => setDepartmentToDelete(null)}
          open={Boolean(departmentToDelete)}
          subtitle={
            departmentToDeleteCanonical
              ? t('organization.deleteDepartmentDialog.subtitle', {
                  name: departmentToDeleteCanonical.name,
                })
              : undefined
          }
          title={t('organization.deleteDepartmentDialog.title')}
        >
          <p className="text-sm leading-6 text-[var(--color-text-muted)]">
            {departmentDeleteBlockReason ??
              t('organization.deleteDepartmentDialog.subtitle', {
                name: departmentToDeleteCanonical?.name ?? '',
              })}
          </p>
        </Modal>
        <Modal
          className="max-w-[520px]"
          closeLabel={t('actions.cancel')}
          footer={
            <>
              <Button
                disabled={addMemberMutation.isPending}
                onClick={() => setAddMemberOpen(false)}
                size="sm"
                variant="secondary"
              >
                {t('actions.cancel')}
              </Button>
              <Button
                disabled={!memberToAdd || isSyncing}
                isLoading={addMemberMutation.isPending}
                onClick={() => {
                  if (!selectedDepartment || !memberToAdd) {
                    return
                  }
                  addMemberMutation.mutate({
                    orgUnitId: selectedDepartment.id,
                    userId: memberToAdd,
                  })
                }}
                size="sm"
              >
                {t('actions.confirm')}
              </Button>
            </>
          }
          onClose={() => setAddMemberOpen(false)}
          open={addMemberOpen}
          subtitle={
            selectedDepartment
              ? t('organization.addMemberDialog.subtitle', { name: selectedDepartment.name })
              : undefined
          }
          title={t('organization.addMemberDialog.title')}
        >
          <div className="grid gap-4">
            <SearchInput
              aria-label={t('organization.addMemberDialog.searchLabel')}
              onChange={(event) => setCandidateSearch(event.target.value)}
              placeholder={t('organization.addMemberDialog.searchPlaceholder')}
              value={candidateSearch}
            />
            <div className="max-h-72 overflow-y-auto rounded-lg border border-[var(--color-border)] p-2">
              {candidateMembersQuery.isLoading ? (
                <div className="grid gap-2">
                  {Array.from({ length: 4 }, (_, index) => (
                    <div
                      aria-hidden
                      className="h-11 animate-pulse rounded-md bg-[var(--color-skeleton)]"
                      key={index}
                    />
                  ))}
                </div>
              ) : candidateMembers.length > 0 ? (
                <ul className="grid gap-1">
                  {candidateMembers.map((member) => (
                    <li key={member.id}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                          memberToAdd === member.id
                            ? 'bg-[rgba(25,91,255,0.1)] text-[var(--color-text)]'
                            : 'hover:bg-[var(--color-hover-muted)]',
                        )}
                      >
                        <input
                          checked={memberToAdd === member.id}
                          className="size-3.5 accent-[var(--color-primary)]"
                          onChange={() => setMemberToAdd(member.id)}
                          type="radio"
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{member.name}</span>
                          <span className="block truncate text-xs text-[var(--color-text-muted)]">
                            {member.email}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
                  {t('organization.addMemberDialog.empty')}
                </p>
              )}
            </div>
          </div>
        </Modal>
      </section>
    </AdminShellPage>
  )
}
